import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, KeyboardEventHandler } from 'react';
import { IconActionButton } from './IconActionButton';
import { type Suggestion, useSuggestions } from './suggest_api';
import { unlessComposing } from './unless_composing';

/**
 * A text input with a suggestion dropdown, following the wiki editor v2
 * metadata search styling: options appear as you type (200ms debounce),
 * mousedown is prevented on rows so the input's blur doesn't close the
 * dropdown before a click lands.
 *
 * The dropdown is a keyboard-navigable combobox: ArrowUp/ArrowDown move the
 * highlight, Enter picks the highlighted option, Escape closes the list.
 * Enter with nothing highlighted falls through to the caller's onKeyDown, so
 * tag-style consumers can still commit typed text.
 *
 * Controlled: the caller owns the text. Picking an option calls onSelect;
 * for name-style fields the caller typically writes the label back into the
 * text, for picker-style fields it clears the text and stores the key.
 */
export function SuggestInput({
   queryKey,
   value,
   onValueChange,
   onSelect,
   fetchSuggestions,
   placeholder,
   ariaLabel,
   excludeKeys = [],
   minQueryLength = 2,
   maxLength,
   className,
   readOnly = false,
   autoFocus = false,
   onKeyDown,
   onBlur,
   listAction,
   searchKey,
   emptyMessage,
   leadingImageUrl,
   imageShape = 'photo',
   clearLabel,
   ariaInvalid,
   ariaDescribedBy,
}: {
   /** Cache-separates this picker from other SuggestInputs on the page. */
   queryKey: string;
   value: string;
   onValueChange: (text: string) => void;
   onSelect: (suggestion: Suggestion) => void;
   fetchSuggestions: (query: string) => Promise<Suggestion[]>;
   placeholder?: string;
   ariaLabel?: string;
   /** Already-chosen entries to hide from the dropdown. */
   excludeKeys?: (number | string)[];
   minQueryLength?: number;
   maxLength?: number;
   className?: string;
   /** readOnly (not disabled) keeps focus through a commit roundtrip. */
   readOnly?: boolean;
   /** For edit-in-place callers that swap this in for rendered text. */
   autoFocus?: boolean;
   /** Commit keys (Enter/comma) and pill-editing keys live with the caller. */
   onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
   /** Fires when the input loses focus, for commit-on-click-off. */
   onBlur?: () => void;
   /** Optional action rendered after the suggestions and included in keyboard navigation. */
   listAction?: { label: string; onSelect: () => void; icon: 'search-plus' | 'search-minus' };
   /** Changing this value reruns the current query, for example after changing search scope. */
   searchKey?: string | number | boolean;
   /** Marks the field invalid, which also gives the input its red error border. */
   ariaInvalid?: boolean;
   /** The id of the message that explains the field, usually its error. */
   ariaDescribedBy?: string;
   /**
    * Shown in the dropdown when a query finds nothing. Without it the
    * combobox closes silently on zero matches.
    */
   emptyMessage?: string;
   /**
    * A thumbnail inside the input's left edge, for picker-style fields whose
    * value is a page with an image — the same image its dropdown row showed.
    */
   leadingImageUrl?: string | null;
   /**
    * What the pictures are, which decides the frame they get. A photo comes
    * out of the image pipeline and is drawn 4:3 and cropped; an icon is
    * square artwork that has to be shown whole, and cropping it to 4:3 takes
    * a quarter of its height off. The flags picker is the icon one.
    * An avatar (people, teams) is cropped round, like the lists beside it.
    */
   imageShape?: 'photo' | 'icon' | 'avatar';
   /**
    * Adds an ✕ inside the input's right edge that empties it, for fields
    * where emptying is an action of its own — a picker whose blank state
    * means "unset" rather than "not typed yet". Names it too, since only the
    * caller knows what the field holds.
    */
   clearLabel?: string;
}) {
   const [isOpen, setIsOpen] = useState(false);
   const inputRef = useRef<HTMLInputElement>(null);
   const [activeIndex, setActiveIndex] = useState(-1);
   const { suggestions, error, isSearching } = useSuggestions(
      queryKey,
      value,
      fetchSuggestions,
      minQueryLength,
      searchKey
   );
   const visible = suggestions.filter(suggestion => !excludeKeys.includes(suggestion.key));
   const listboxId = useId();
   const optionId = (index: number) => `${listboxId}-option-${index}`;
   // A pending search keeps the dropdown open so a slow response doesn't
   // leave the combobox silent after typing.
   const hasSearchQuery = value.trim().length >= minQueryLength;
   const optionCount = visible.length + (listAction ? 1 : 0);
   const showEmptyMessage = emptyMessage !== undefined && !isSearching && visible.length === 0;
   const isExpanded =
      isOpen &&
      hasSearchQuery &&
      (visible.length > 0 || isSearching || listAction !== undefined || showEmptyMessage);
   const activeOptionId =
      isExpanded && activeIndex >= 0 && activeIndex < optionCount
         ? optionId(activeIndex)
         : undefined;
   // Nothing to clear on an empty field, and a readOnly one is mid-commit.
   const showClear = clearLabel !== undefined && value !== '' && !readOnly;
   // 4:3 for a photo, because that is the shape the image pipeline delivers:
   // mini arrives already cropped to 56x42, and thumbnail keeps whatever
   // shape the upload had inside 96x72, which object-cover then crops from
   // the centre the same way. An icon keeps its square and is fitted, not
   // cropped. An avatar is cropped round, because the default avatars are
   // square artwork that a 4:3 frame would trim top and bottom.
   const isPhoto = imageShape === 'photo';
   const imageClass = {
      photo: 'aspect-[4/3] rounded border border-gray-200 bg-white object-cover',
      icon: 'aspect-square rounded border border-gray-200 bg-white p-0.5 object-contain',
      avatar: 'aspect-square rounded-full object-cover',
   }[imageShape];

   // Keep the highlighted option in view when arrowing past the visible slice.
   useEffect(() => {
      if (activeOptionId) {
         document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
      }
   }, [activeOptionId]);

   const select = (suggestion: Suggestion) => {
      // A browse-style field (minQueryLength 0) lists its whole set on focus
      // and exists to add several in a row. Picking keeps focus in the input,
      // and only a *new* focus reopens the list, so closing here would strand
      // the user: clicking the same input again fires no focus event. Leave it
      // open — the picked option drops out of the list on the next render.
      setIsOpen(minQueryLength === 0);
      setActiveIndex(-1);
      onSelect(suggestion);
   };

   // Wrapped so a composing IME keeps Enter, Escape, and the arrows — for
   // the dropdown here and for every caller-passed commit handler below.
   const handleKeyDown = unlessComposing((event: KeyboardEvent<HTMLInputElement>) => {
      if (isExpanded) {
         if (event.key === 'ArrowDown' && optionCount > 0) {
            event.preventDefault();
            setActiveIndex(index => (index + 1) % optionCount);
            return;
         }
         if (event.key === 'ArrowUp' && optionCount > 0) {
            event.preventDefault();
            setActiveIndex(index => (index <= 0 ? optionCount - 1 : index - 1));
            return;
         }
         if (event.key === 'Enter' && activeIndex >= 0 && activeIndex < optionCount) {
            event.preventDefault();
            if (activeIndex < visible.length) {
               select(visible[activeIndex]);
            } else {
               setActiveIndex(-1);
               listAction?.onSelect();
            }
            return;
         }
         if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            setActiveIndex(-1);
            return;
         }
      }
      // Enter with nothing highlighted, commit/pill-editing keys, etc. stay
      // with the caller.
      onKeyDown?.(event);
   });

   return (
      <div className={className}>
         <div className="relative">
            {leadingImageUrl && (
               // A step smaller than a dropdown row: here the page is already
               // chosen and the picture only confirms it, while a row is
               // where five near-identical phones are told apart.
               <Picture
                  key={leadingImageUrl}
                  src={leadingImageUrl}
                  width={isPhoto ? 37 : 28}
                  height={28}
                  className={cn(
                     'pointer-events-none absolute left-1.5 top-1/2 h-7 -translate-y-1/2',
                     imageClass
                  )}
               />
            )}
            <Input
               ref={inputRef}
               id={`${listboxId}-input`}
               // No explicit type: Carpenter's base layer gives every
               // input[type] the browser's font size, and the plain Input
               // beside this one in a form does not carry the attribute.
               autoComplete="off"
               // A picker that shows thumbnails is taller, so the image
               // has room around it; the height holds while the field is
               // empty, so it does not jump when the image comes and goes.
               className={cn(
                  leadingImageUrl !== undefined && 'h-10',
                  leadingImageUrl && (isPhoto ? 'pl-12' : 'pl-10'),
                  showClear && 'pr-9'
               )}
               // Every SuggestInput field holds names and identifiers (items,
               // tags, teams, guide titles), not prose; spellcheck squiggles
               // on product names read as errors.
               spellCheck={false}
               autoFocus={autoFocus}
               role="combobox"
               aria-expanded={isExpanded}
               aria-controls={listboxId}
               aria-autocomplete="list"
               aria-activedescendant={activeOptionId}
               value={value}
               placeholder={placeholder}
               aria-label={ariaLabel}
               aria-invalid={ariaInvalid || undefined}
               aria-describedby={ariaDescribedBy}
               maxLength={maxLength}
               readOnly={readOnly}
               onChange={event => {
                  setIsOpen(true);
                  // A new query invalidates the old highlight; start each
                  // result set with nothing highlighted.
                  setActiveIndex(-1);
                  onValueChange(event.target.value);
               }}
               // Zero-length pickers use focus as their explicit "list all"
               // action. Other inputs still open only after typing, so
               // focusing a filled item row does not cover its controls.
               onFocus={() => {
                  if (minQueryLength === 0) {
                     setIsOpen(true);
                  }
               }}
               onBlur={() => {
                  setIsOpen(false);
                  onBlur?.();
               }}
               onKeyDown={handleKeyDown}
            />
            {showClear && (
               <IconActionButton
                  label={clearLabel}
                  icon="xmark"
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => {
                     onValueChange('');
                     // Clearing is usually a prelude to typing a replacement,
                     // and the click may have come from outside the field.
                     inputRef.current?.focus();
                  }}
                  // size-9 on a touch screen: 28px is a small target for a
                  // thumb, and the dropdown rows grow the same way below.
                  className="absolute right-1.5 top-1/2 size-7 -translate-y-1/2 text-gray-400 any-pointer-coarse:size-9 hover:text-gray-600"
               />
            )}
            {isExpanded && (
               <div
                  id={listboxId}
                  role="listbox"
                  className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg"
               >
                  {visible.map((suggestion, index) => (
                     <button
                        key={suggestion.key}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        type="button"
                        className={cn(
                           'flex w-full items-center gap-2 px-3 py-2 text-left text-sm any-pointer-coarse:py-3 hover:bg-brand-50',
                           index === activeIndex && 'bg-brand-50'
                        )}
                        // Keep focus on the input so its onBlur doesn't close
                        // the dropdown before this click lands.
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => select(suggestion)}
                     >
                        {suggestion.imageUrl && (
                           // No loading="lazy": the list holds at most a
                           // screenful and every row of it is on screen the
                           // moment it opens, so deferring only delays them.
                           <Picture
                              key={suggestion.imageUrl}
                              src={suggestion.imageUrl}
                              width={isPhoto ? 43 : 32}
                              height={32}
                              className={cn('h-8 shrink-0', imageClass)}
                           />
                        )}
                        <span className="min-w-0 flex-1">
                           <span className="block truncate">{suggestion.label}</span>
                           {suggestion.description && (
                              <span className="block truncate text-xs text-gray-600">
                                 {suggestion.description}
                              </span>
                           )}
                        </span>
                     </button>
                  ))}
                  {isSearching && visible.length === 0 && (
                     <div aria-live="polite" className="px-3 py-2 text-sm text-gray-500">
                        {_js('Searching...')}
                     </div>
                  )}
                  {showEmptyMessage && (
                     <div aria-live="polite" className="px-3 py-2 text-sm text-gray-500">
                        {emptyMessage}
                     </div>
                  )}
                  {listAction && (
                     <button
                        id={optionId(visible.length)}
                        role="option"
                        aria-selected={activeIndex === visible.length}
                        type="button"
                        className={cn(
                           'flex w-full items-center justify-center gap-2 border-t border-gray-200 px-3 py-2 text-sm font-medium text-brand-600 any-pointer-coarse:py-3 hover:bg-brand-50',
                           activeIndex === visible.length && 'bg-brand-50'
                        )}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                           setActiveIndex(-1);
                           listAction.onSelect();
                        }}
                     >
                        <FaIcon name={listAction.icon} className="size-3.5" />
                        {listAction.label}
                     </button>
                  )}
               </div>
            )}
         </div>
         {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
   );
}

/**
 * Holds the frame when the file 404s: a dead rendition draws as a zero-width
 * img, and the label sliding left reads as a row that has no picture.
 */
function Picture({
   src,
   width,
   height,
   className,
}: {
   src: string;
   width: number;
   height: number;
   className?: string;
}) {
   const [failed, setFailed] = useState(false);
   if (failed) {
      return <span aria-hidden className={cn(className, 'block bg-gray-100')} />;
   }
   return (
      <img
         src={src}
         alt=""
         width={width}
         height={height}
         className={className}
         onError={() => setFailed(true)}
      />
   );
}
